/**
 * Client-only Yak Trails tuning. Competitive rules live in yak-trails-core's
 * versioned config; every presentation, input, transport, and adaptive-quality
 * value that changes the feel of the client is collected here.
 */
export const YAK_TRAILS_CLIENT_TUNING = {
  startup: {
    bootstrapRetryDelaysMs: [0, 350, 1_000],
    profileRefreshDelaysMs: [350, 900, 1_800],
    reconnectCountdownIntervalMs: 250,
    respawnRetryPaddingMs: 75,
    practiceWorkerStartupTimeoutMs: 8_000,
  },
  controls: {
    directionDeadZone: 0.08,
    minimumSendIntervalMs: 55,
  },
  transport: {
    latencySmoothingPreviousWeight: 0.75,
    degradedLatencyMs: 220,
    heartbeatIntervalMs: 2_000,
    foregroundHealthTimeoutMs: 1_500,
    reconnectInitialDelayMs: 250,
    reconnectMaximumDelayMs: 4_000,
  },
  motion: {
    maximumExtrapolationSeconds: 0.45,
    maximumFrameSeconds: 0.05,
    maximumLatencyLeadSeconds: 0.35,
    hardReconciliationDistance: 7,
    directionEpsilon: 0.0001,
    correctionDeadZone: 0.025,
    selfReconciliationRate: 5,
    remoteReconciliationRate: 8,
    selfPresentationLeadSeconds: 0.045,
    remotePresentationLeadSeconds: 0.02,
  },
  camera: {
    height: 24,
    zOffset: 18,
    initialZoom: 24,
    near: 0.1,
    far: 160,
    headingLead: 1.35,
    teleportDistance: 14,
    followSmoothing: 10,
    zoomSmoothing: 8,
    zoomPerTerritoryPercent: 0.035,
    minimumZoom: 20.5,
    maximumZoom: 24,
  },
  showcase: {
    cameraPosition: [3.8, 3.5, 5.5] as [number, number, number],
    cameraTargetY: 0.34,
    maximumZoom: 64,
    minimumVerticalWorldSpan: 2.05,
    initialZoom: 48,
    near: 0.1,
    far: 30,
    dpr: [1, 1.5] as [number, number],
    ambientIntensity: 1.8,
    directionalPosition: [3, 6, 5] as [number, number, number],
    directionalIntensity: 2.4,
    modelRotation: [-0.08, 0.15, 0] as [number, number, number],
    modelHeading: Math.atan2(5.5, 3.8),
    modelScale: 1.08,
    modelScaleBySkin: { unicorn: 0.82 } as Readonly<Partial<Record<string, number>>>,
  },
  quality: {
    mobileWidthBreakpoint: 600,
    highDensityCutoff: 2,
    sampleCount: 120,
    downgradeAverageFrameMs: 20,
    upgradeAverageFrameMs: 16.2,
    highDpr: [1, 1.5] as [number, number],
    lowDpr: 1,
  },
  player: {
    elevation: 0.72,
    shadowOffsetY: -0.56,
    shadowRadius: 0.72,
    shadowSegments: 20,
    shadowOpacity: 0.18,
    leanSmoothing: 18,
    leanAmount: 0.045,
    selfScale: 0.9,
    remoteScale: 0.82,
    markerHeight: 1.22,
    markerRadius: 0.18,
    markerEmissiveIntensity: 0.18,
    skinHeadingSmoothing: 18,
    idleBobSpeed: 5,
    idleBobAmount: 0.07,
    idleTwistSpeed: 3,
    idleTwistAmount: 0.025,
  },
  arena: {
    backgroundColor: "#dffcf4",
    fogNear: 54,
    fogFar: 96,
    skyLightColor: "#fffdf0",
    groundLightColor: "#93d8ce",
    hemisphereIntensity: 1.55,
    directionalX: 20,
    directionalY: 34,
    directionalZ: 16,
    directionalIntensity: 2.15,
    shadowMapSize: 1_024,
    shadowCameraExtent: 30,
    lowerFloorY: -0.42,
    lowerFloorTopRadiusOffset: 0.4,
    lowerFloorBottomRadiusOffset: 0.9,
    lowerFloorDepth: 0.85,
    upperFloorY: -0.28,
    upperFloorRadiusOffset: 0.05,
    upperFloorDepth: 0.34,
    cylinderSegments: 96,
    lowerFloorColor: "#c8f8ec",
    upperFloorColor: "#e8fff8",
    lowerFloorRoughness: 0.9,
    upperFloorRoughness: 0.82,
  },
  territory: {
    surfaceY: 0.31,
    trailContactY: 0.325,
    trailSurfaceY: 0.365,
    meshBottomY: -0.03,
    contourSmoothingPasses: 2,
    contourCornerWeight: 0.8,
    simplificationEpsilon: 0.0001,
  },
  trail: {
    contactHalfWidth: 0.52,
    surfaceHalfWidth: 0.45,
    tipVisibilityDistance: 0.02,
    tipOverlap: 0.08,
    tipContactDepth: 0.018,
    tipContactWidth: 1.04,
    tipSurfaceDepth: 0.025,
    tipSurfaceWidth: 0.9,
    contactLightnessOffset: -0.2,
    contactRoughness: 0.82,
    surfaceRoughness: 0.68,
    polygonOffsetFactor: -2,
  },
  effects: {
    captureParticlesHigh: 12,
    captureParticlesLow: 6,
    deathParticlesHigh: 14,
    deathParticlesLow: 7,
    eliminationPopSeconds: 0.16,
    eliminationVanishSeconds: 0.48,
    eliminationPopScale: 0.16,
    eliminationPopLift: 0.22,
    eliminationVanishLift: 0.32,
    eliminationSpinSpeed: 8.5,
    eliminationWobbleSpeed: 13,
    eliminationWobbleAmount: 0.16,
    deathCubeSeconds: 1.45,
    deathCubeDelaySeconds: 0.12,
    sparkleSeconds: 1.1,
    sparkleGravity: 2.6,
    deathCubeGravity: 3.7,
    burstBaseSpeed: 1.3,
    burstSpeedStep: 0.22,
    burstBaseLift: 1.25,
    burstLiftStep: 0.18,
    deathBurstHorizontalMultiplier: 1.5,
    deathBurstExtraLift: 1,
    deathBurstAlternatingLift: 0.4,
  },
  minimap: {
    pixels: 100,
    ownershipSampleStep: 4,
    selfRadius: 3,
    remoteRadius: 2,
    selfLineWidth: 1.5,
    remoteLineWidth: 0.7,
  },
} as const;
